/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef } from "react";
import { Box, useTheme } from "@mui/material";
import { EditorContent, JSONContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { mergeAttributes, Node as CoreNode } from "@tiptap/core";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { PluginKey } from "@tiptap/pm/state";
import { v4 as uuidv4 } from "uuid";
import MentionEditorToolbar from "./MentionEditorToolbar";
import { mentionRenderOptions } from "./MentionSuggestionOptions";
import { PromptDataType } from "../../types/prompt-model";
import {
  ANSWER_LIST_FORMATS,
  buildReferenceFieldId,
  getPromptTopProperties,
  LIST_AS_OPTION,
  PromptMentionSource,
  REFERENCE_FIELD_MENTIONS,
  referenceFieldDotPath,
  ReferenceMentionItem,
} from "./types";

export interface MentionEditorProps {
  /** TipTap document JSON (the stored template). */
  value?: JSONContent | null;
  /** Called with `editor.getJSON()` on every update. */
  onChange: (json: JSONContent) => void;
  placeholder?: string;
  /** CSS min-height for the editable area. Defaults to "80px". */
  minHeight?: string;
  disabled?: boolean;
  /**
   * The prompts an "@" mention can reference. Typing "@" lists these; the "."
   * that follows offers each prompt's fields (branching on its data type). The
   * editor reads the latest set through a ref, so re-selecting prompts never
   * rebuilds the editor (which would drop the author's content).
   */
  prompts?: PromptMentionSource[];
  /**
   * Old single-prompt experience: instead of the "@"/"." cascade, the editor
   * offers the legacy "@" field mention (Answer / Quote / Document Name) that
   * resolves against the reference's one selected prompt. `prompts` is ignored.
   */
  singlePrompt?: boolean;
  /**
   * The single prompt's data type (single-prompt mode only). When it is
   * ListOfValues, picking "Answer" drills into the List As list options,
   * emitting bare field ids that resolve against the reference's own prompt.
   */
  anchorPromptDataType?: PromptDataType;
}

/**
 * A TipTap rich-text editor with the cascading reference-mention model: "@"
 * picks a prompt, "." picks one of its fields. Plus a lean formatting toolbar
 * (bold, italic, underline, bullet/numbered list).
 */
const MentionEditor: React.FC<MentionEditorProps> = ({
  value,
  onChange,
  placeholder,
  minHeight = "80px",
  disabled = false,
  prompts = [],
  singlePrompt = false,
  anchorPromptDataType,
}) => {
  const theme = useTheme();

  // The mention extensions close over this ref rather than the `prompts` prop
  // so the editor instance is stable across prompt-selection changes.
  const promptsRef = useRef<PromptMentionSource[]>(prompts);
  useEffect(() => {
    promptsRef.current = prompts;
  }, [prompts]);

  // The single-prompt "@" picker reads the anchor prompt's data type live
  // (same reason as promptsRef) to decide whether "Answer" offers List As.
  const anchorPromptDataTypeRef = useRef(anchorPromptDataType);
  useEffect(() => {
    anchorPromptDataTypeRef.current = anchorPromptDataType;
  }, [anchorPromptDataType]);

  // Built once; the suggestion lists read live data from promptsRef / editor
  // state, so the extensions never need rebuilding.
  const extensions = useMemo(() => {
    const decorate = (
      options: { id: string; label: string }[],
      base: Partial<ReferenceMentionItem>
    ): ReferenceMentionItem[] =>
      options.map((option) => ({
        // Freshly-listed suggestions carry no uuid; the `command` handler mints
        // one only when the item is actually inserted as a node.
        uuid: null,
        id: option.id,
        label: option.label,
        type: "property",
        ...base,
      }));

    const PromptMention = Mention.extend({
      name: "mentionedPromptAnswer",
      addAttributes() {
        return {
          uuid: { default: null },
          id: { default: null },
          label: { default: null },
          type: { default: null },
          promptDataType: { default: null },
          renderAs: { default: "label" },
          properties: { default: [] },
        };
      },
      renderHTML({ node, HTMLAttributes }) {
        return [
          "span",
          mergeAttributes({ class: "mention" }, HTMLAttributes),
          `@${node.attrs.label ?? node.attrs.id ?? ""}`,
        ];
      },
      renderText({ node }) {
        return `@${node.attrs.label ?? ""}`;
      },
    }).configure({
      HTMLAttributes: { class: "mention" },
      suggestion: {
        ...mentionRenderOptions,
        char: "@",
        pluginKey: new PluginKey("mentionedPromptAnswer"),
        items: ({ query }): ReferenceMentionItem[] => {
          const searchText = query.split(".")[0].toLowerCase();
          return promptsRef.current
            .filter((prompt) => prompt.label.toLowerCase().includes(searchText))
            .map((prompt) => ({
              uuid: null,
              id: prompt.objectIdentifier,
              label: prompt.label,
              type: "filter-answer",
              promptDataType: prompt.promptDataType,
            }));
        },
        command: ({ editor, range, props }) => {
          const attrs = props as ReferenceMentionItem;
          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: "mentionedPromptAnswer",
                attrs: {
                  uuid: uuidv4(),
                  id: attrs.id,
                  label: attrs.label,
                  type: "filter-answer",
                  promptDataType: attrs.promptDataType,
                  renderAs: "label",
                  properties: [],
                },
              },
            ])
            .run();
          // Chain straight into the field picker.
          editor.chain().focus().insertContent(".").run();
        },
      },
    });

    // The "." cascade picker. It offers different options depending on what the
    // dot follows:
    //   - after "@prompt"                → the prompt's fields (Answer/Document/…)
    //   - after the "Answer" step        → the list options (Bulleted/Numbered/
    //     (list-of-values prompts only)     Text)
    // Every terminal pick collapses the whole "@prompt…" run into a single flat
    // `referenceFieldMention` chip whose id encodes the field and prompt as
    // `{field}::{promptObjectId}` — the only shape the server-side reference
    // renderer resolves (it keys fields off `id`, since only id/label survive
    // the backends' JSON naming policy). Picking "Answer" on a list-of-values
    // prompt is the one non-terminal step: it drops a transient `mentionProperty`
    // marker (carrying the prompt link) and auto-opens the list options — the
    // same auto-"." the field picker uses, so it fires reliably without a
    // manually re-typed dot on a finished chip.
    const PropertyMention = Mention.extend({
      name: "mentionProperty",
      addAttributes() {
        return {
          uuid: { default: null },
          id: { default: null },
          label: { default: null },
          type: { default: null },
          parentMentionId: { default: null },
          promptDataType: { default: null },
          renderAs: { default: "label" },
        };
      },
      renderHTML({ node, HTMLAttributes }) {
        return [
          "span",
          mergeAttributes({ class: "mention-property" }, HTMLAttributes),
          `.${node.attrs.label ?? node.attrs.id ?? ""}`,
        ];
      },
      renderText({ node }) {
        return `.${node.attrs.label ?? ""}`;
      },
    }).configure({
      HTMLAttributes: { class: "mention-property" },
      suggestion: {
        ...mentionRenderOptions,
        char: ".",
        pluginKey: new PluginKey("mentionProperty"),
        items: ({ editor }): ReferenceMentionItem[] => {
          const { state } = editor;
          const { from } = state.selection;

          // Walk backwards from the dot over any intervening text to the chip /
          // node it hangs off; only text may sit between, so anything else ends
          // the run with no suggestions.
          const $pos = state.doc.resolve(from - 1);
          let index = $pos.index();

          while (index >= 0) {
            // `$pos.index()` can equal `childCount` when the dot sits at the end
            // of its parent; skip that out-of-range slot before walking back.
            if (index >= $pos.parent.childCount) {
              index--;
              continue;
            }

            const node = $pos.parent.child(index);

            if (node.type.name === "mentionedPromptAnswer") {
              return decorate(getPromptTopProperties(node.attrs.promptDataType), {
                parentMentionId: node.attrs.uuid,
                promptDataType: node.attrs.promptDataType,
              });
            }

            if (node.type.name === "mentionProperty" && node.attrs.id === "answer") {
              // The transient "Answer" step: offer the single "List As" step,
              // keeping the prompt link so the pick can find the "@prompt".
              return decorate([LIST_AS_OPTION], {
                parentMentionId: node.attrs.parentMentionId,
              });
            }

            if (node.type.name === "mentionProperty" && node.attrs.id === LIST_AS_OPTION.id) {
              // The transient "List As" step: offer the list options.
              return decorate(ANSWER_LIST_FORMATS, {
                parentMentionId: node.attrs.parentMentionId,
              });
            }

            if (node.isText) {
              index--;
              continue;
            }

            return [];
          }
          return [];
        },
        command: ({ editor, range, props }) => {
          const attrs = props as ReferenceMentionItem;

          // Non-terminal drill steps: drop a transient marker (keeping the prompt
          // link) and auto-open the next picker — the reliable auto-"." the field
          // picker uses. "Answer" on a list-of-values prompt opens the "List As"
          // step; "List As" opens the list options.
          const opensListAsStep =
            attrs.id === "answer" && attrs.promptDataType === PromptDataType.ListOfValues;
          const opensFormatStep = attrs.id === LIST_AS_OPTION.id;

          if (opensListAsStep || opensFormatStep) {
            editor
              .chain()
              .focus()
              .insertContentAt(range, [
                {
                  type: "mentionProperty",
                  attrs: {
                    uuid: uuidv4(),
                    id: opensFormatStep ? LIST_AS_OPTION.id : "answer",
                    label: attrs.label,
                    type: "property",
                    parentMentionId: attrs.parentMentionId,
                    promptDataType: null,
                    renderAs: "label",
                  },
                },
              ])
              .insertContent(".")
              .run();
            return;
          }

          // Terminal pick (a field, or a list format). Both carry the prompt's
          // uuid in `parentMentionId`; find that still-present "@prompt" node and
          // replace the whole run up to the picked query with one flat chip.
          let startPos: number | null = null;
          let promptObjectId: string | null = null;
          let promptLabel = "";
          editor.state.doc.descendants((node, pos) => {
            if (
              node.type.name === "mentionedPromptAnswer" &&
              node.attrs.uuid === attrs.parentMentionId
            ) {
              startPos = pos;
              promptObjectId = node.attrs.id;
              promptLabel = node.attrs.label ?? "";
              return false;
            }
            return true;
          });

          if (startPos === null || !promptObjectId) {
            return;
          }

          const runStart = startPos as number;
          const promptId = promptObjectId as string;
          const flatId = buildReferenceFieldId(attrs.id, promptId);
          // Label reads as the dot-notation path (e.g. "Prompt.answer.list-as.
          // numbered"); the id stays the flat form the renderer resolves.
          const flatLabel = `${promptLabel}.${referenceFieldDotPath(attrs.id)}`;

          // Replace [run start … picked query] with the flattened field chip.
          editor
            .chain()
            .focus()
            .insertContentAt({ from: runStart, to: range.to }, [
              { type: "referenceFieldMention", attrs: { id: flatId, label: flatLabel } },
              { type: "text", text: " " },
            ])
            .run();
        },
      },
    });

    // Legacy "@" field mention — render-only, no "@"-style picker. New content
    // uses the "@"/"." cascade, but templates authored before it still carry
    // `referenceFieldMention` nodes; registering the node type (without a
    // suggestion, so "@" no longer inserts anything) keeps those rendering in
    // blue and round-tripping through edit/save so they continue to export.
    const LegacyFieldMention = CoreNode.create({
      name: "referenceFieldMention",
      group: "inline",
      inline: true,
      atom: true,
      selectable: true,
      addAttributes() {
        return {
          id: { default: null },
          label: { default: null },
        };
      },
      parseHTML() {
        return [{ tag: "span[data-reference-field-mention]" }];
      },
      renderHTML({ node, HTMLAttributes }) {
        return [
          "span",
          mergeAttributes({ class: "mention", "data-reference-field-mention": "" }, HTMLAttributes),
          `@${node.attrs.label ?? node.attrs.id ?? ""}`,
        ];
      },
      renderText({ node }) {
        return `@${node.attrs.label ?? node.attrs.id ?? ""}`;
      },
    });

    // Old single-prompt experience: the "@" field mention is an active picker
    // (Answer / Quote / Document Name), resolving against the reference's one
    // selected prompt. Inserts the same `referenceFieldMention` node the
    // render-only variant above reads, so both modes render identically.
    const LegacyFieldMentionPicker = Mention.extend({
      name: "referenceFieldMention",
      addAttributes() {
        return {
          id: { default: null },
          label: { default: null },
        };
      },
      renderHTML({ node, HTMLAttributes }) {
        return [
          "span",
          mergeAttributes({ class: "mention", "data-reference-field-mention": "" }, HTMLAttributes),
          `@${node.attrs.label ?? node.attrs.id ?? ""}`,
        ];
      },
      renderText({ node }) {
        return `@${node.attrs.label ?? node.attrs.id ?? ""}`;
      },
    }).configure({
      HTMLAttributes: { class: "mention" },
      suggestion: {
        ...mentionRenderOptions,
        char: "@",
        pluginKey: new PluginKey("referenceFieldMention"),
        items: ({ query }): ReferenceMentionItem[] => {
          const lower = query.toLowerCase();
          return REFERENCE_FIELD_MENTIONS.filter((m) =>
            m.label.toLowerCase().includes(lower)
          ).map((m) => ({ uuid: null, id: m.id, label: m.label, type: "reference-field" }));
        },
        command: ({ editor, range, props }) => {
          const attrs = props as ReferenceMentionItem;

          // A list-of-values answer drills into "@answer.list-as.{format}". The
          // head "answer" marker is labelled so it renders "@answer" (there's no
          // "@prompt" before it in single-prompt mode). Other fields insert their
          // terminal chip directly.
          if (
            attrs.id === "answer" &&
            anchorPromptDataTypeRef.current === PromptDataType.ListOfValues
          ) {
            editor
              .chain()
              .focus()
              .insertContentAt(range, [
                {
                  type: "mentionProperty",
                  attrs: {
                    uuid: uuidv4(),
                    id: "answer",
                    label: "answer",
                    type: "property",
                    parentMentionId: null,
                    promptDataType: null,
                    renderAs: "label",
                  },
                },
              ])
              .insertContent(".")
              .run();
            return;
          }

          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              { type: "referenceFieldMention", attrs: { id: attrs.id, label: attrs.label } },
              { type: "text", text: " " },
            ])
            .run();
        },
      },
    });

    // Single-prompt "." picker: the List As drill for the legacy "@" Answer.
    // Mirrors the multi-prompt cascade (Answer → List As → Bulleted / Numbered /
    // Text) but collapses to a BARE `referenceFieldMention` (no "::promptId"),
    // since a single-prompt mention resolves against the reference's own prompt.
    // The head "answer" step renders "@answer"; the chained steps render ".x".
    const SinglePromptListPicker = Mention.extend({
      name: "mentionProperty",
      addAttributes() {
        return {
          uuid: { default: null },
          id: { default: null },
          label: { default: null },
          type: { default: null },
          parentMentionId: { default: null },
          promptDataType: { default: null },
          renderAs: { default: "label" },
        };
      },
      renderHTML({ node, HTMLAttributes }) {
        const prefix = node.attrs.id === "answer" ? "@" : ".";
        return [
          "span",
          mergeAttributes({ class: "mention-property" }, HTMLAttributes),
          `${prefix}${node.attrs.label ?? node.attrs.id ?? ""}`,
        ];
      },
      renderText({ node }) {
        const prefix = node.attrs.id === "answer" ? "@" : ".";
        return `${prefix}${node.attrs.label ?? ""}`;
      },
    }).configure({
      HTMLAttributes: { class: "mention-property" },
      suggestion: {
        ...mentionRenderOptions,
        char: ".",
        pluginKey: new PluginKey("singlePromptProperty"),
        items: ({ editor }): ReferenceMentionItem[] => {
          const { state } = editor;
          const { from } = state.selection;
          const $pos = state.doc.resolve(from - 1);
          let index = $pos.index();

          while (index >= 0) {
            if (index >= $pos.parent.childCount) {
              index--;
              continue;
            }

            const node = $pos.parent.child(index);
            if (node.type.name === "mentionProperty" && node.attrs.id === "answer") {
              return decorate([LIST_AS_OPTION], {});
            }
            if (node.type.name === "mentionProperty" && node.attrs.id === LIST_AS_OPTION.id) {
              return decorate(ANSWER_LIST_FORMATS, {});
            }
            if (node.isText) {
              index--;
              continue;
            }
            return [];
          }
          return [];
        },
        command: ({ editor, range, props }) => {
          const attrs = props as ReferenceMentionItem;

          // "List As" is non-terminal: drop a transient marker (rendering
          // ".list-as") and auto-open the format picker.
          if (attrs.id === LIST_AS_OPTION.id) {
            editor
              .chain()
              .focus()
              .insertContentAt(range, [
                {
                  type: "mentionProperty",
                  attrs: {
                    uuid: uuidv4(),
                    id: LIST_AS_OPTION.id,
                    label: LIST_AS_OPTION.id,
                    type: "property",
                    parentMentionId: null,
                    promptDataType: null,
                    renderAs: "label",
                  },
                },
              ])
              .insertContent(".")
              .run();
            return;
          }

          // Terminal format: collapse the run (back to the "@answer" marker) into
          // one bare field chip whose label reads as the dot-notation path.
          let startPos: number | null = null;
          editor.state.doc.descendants((node, pos) => {
            if (
              pos < range.from &&
              node.type.name === "mentionProperty" &&
              node.attrs.id === "answer"
            ) {
              startPos = pos;
            }
            return true;
          });

          if (startPos === null) {
            return;
          }

          const runStart = startPos as number;
          editor
            .chain()
            .focus()
            .insertContentAt({ from: runStart, to: range.to }, [
              {
                type: "referenceFieldMention",
                attrs: { id: attrs.id, label: referenceFieldDotPath(attrs.id) },
              },
              { type: "text", text: " " },
            ])
            .run();
        },
      },
    });

    if (singlePrompt) {
      return [
        StarterKit,
        Underline,
        Placeholder.configure({ placeholder: placeholder ?? "" }),
        LegacyFieldMentionPicker,
        SinglePromptListPicker,
      ];
    }

    return [
      StarterKit,
      Underline,
      Placeholder.configure({ placeholder: placeholder ?? "" }),
      PromptMention,
      PropertyMention,
      LegacyFieldMention,
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeholder, singlePrompt]);

  const editor = useEditor(
    {
      editable: !disabled,
      editorProps: {
        attributes: {
          class: "muiStyle",
        },
      },
      extensions,
      content: value ?? undefined,
      onUpdate: ({ editor: updatedEditor }) => {
        onChange(updatedEditor.getJSON());
      },
    },
    [extensions]
  );

  // Keep the editor's editable state in sync with the `disabled` prop,
  // since `useEditor` does not re-run when props change.
  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  if (!editor) {
    return null;
  }

  const accent = theme.palette.primary.main;

  return (
    <Box
      sx={{
        overflow: "hidden",
        ".muiStyle": {
          minHeight,
          maxHeight: "500px",
          resize: "vertical",
          border: "1px solid",
          borderColor: theme.palette.grey[400],
          padding: "10px",
          borderRadius: "4px",
          fontSize: "13px",
          fontFamily: "Inter, sans-serif",
          overflow: "auto",
          lineHeight: "normal",
          backgroundColor: disabled ? theme.palette.action.disabledBackground : theme.palette.grey[50],
          color: theme.palette.text.primary,
          "&:focus-within": {
            outline: "1.5px solid",
            outlineColor: accent,
            borderColor: accent,
          },
          "&:hover": {
            borderColor: disabled ? theme.palette.grey[400] : theme.palette.text.primary,
          },
        },
        // Reference mentions render blue (matching the basic assembly editor)
        // so authors can tell them apart from plain template text: the "@"
        // prompt in the primary blue, its "." fields in a darker blue.
        ".mention": {
          color: theme.palette.primary.main,
          fontWeight: 600,
        },
        ".mention-property": {
          color: theme.palette.primary.dark,
          fontWeight: 600,
        },
        ".ProseMirror p.is-editor-empty:first-of-type::before": {
          content: "attr(data-placeholder)",
          float: "left",
          color: theme.palette.text.disabled,
          pointerEvents: "none",
          height: 0,
        },
        ".ProseMirror p": {
          margin: 0,
        },
        ".ProseMirror:focus": {
          outline: "none",
        },
      }}
    >
      {!disabled && <MentionEditorToolbar editor={editor} disabled={disabled} />}
      <EditorContent editor={editor} />
    </Box>
  );
};

export default MentionEditor;
